import { Role, rolesManipulator } from 'auth';
import classNames from 'classnames';
import { FormikProps, FormikProvider } from 'formik';
import { ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Tooltip, TooltipNew } from 'ui';

export interface OnboardingHolderWrapperProps {
  title?: string;
  titleHint?: string;
  children: ReactNode;
  onClickContinue?: () => void;
  onClickSkip?: () => void;
  buttonSkipName?: string;
  buttonContinueHint?: string | ReactNode;
  buttonContinueName?: string;
  messageInsteadButtons?: string | ReactNode;
  formik?: FormikProps<any>;
  isLoading?: boolean;
  disableNext?: boolean;
  className?: string;
  isConfirmPage?: boolean;
}

export const OnboardingHolderWrapper = ({
  title,
  children,
  onClickContinue,
  formik,
  isLoading,
  disableNext,
  onClickSkip,
  className,
  buttonSkipName = 'Skip',
  buttonContinueName = 'Continue',
  buttonContinueHint,
  titleHint,
  messageInsteadButtons,
  isConfirmPage = false,
}: OnboardingHolderWrapperProps) => {
  const { state } = useLocation();
  const isIssuerAdmin = rolesManipulator.current_user_role === Role.SR_ISSUER_ADMIN;
  const isUpdate = state?.action === 'update';
  const hintSaveButtonToRequest = useMemo<string>(() => {
    if (!isUpdate || isIssuerAdmin) return '';
    return `Please note! In order for this information to change on the holder's page, ${
      !isConfirmPage ? 'it must be confirmed on the confirmation tab and' : ''
    } for the Issuer admin to approve it on the Holders page in the "Edit requests" tab.`;
  }, [isConfirmPage, isIssuerAdmin, isUpdate]);

  const Body = (
    <div
      className={classNames(
        'flex flex-col space-y-10 min-h-[500px]',
        {
          'w-full': !className?.includes('w-'),
        },
        className,
      )}
    >
      {title && (
        <TooltipNew text={titleHint || ''} disabled={!titleHint}>
          <span
            style={{ wordBreak: 'break-word' }}
            className={classNames('text-[18px] leading-6 font-[500] font-[Inter] break-words', {
              'cursor-help': !!titleHint,
            })}
          >
            {title}
          </span>
        </TooltipNew>
      )}
      {children}
      <div className="w-full flex flex-col items-center justify-between gap-4">
        {messageInsteadButtons ? (
          <span className="w-full text-left text-xl text-blue-700">{messageInsteadButtons}</span>
        ) : (
          <>
            {onClickContinue && (
              <Tooltip
                position="up"
                positionHorizontal="center"
                variant="gray"
                classNameTooltip="border"
                className="w-full"
                button={
                  <TooltipNew
                    text={hintSaveButtonToRequest}
                    disabled={!hintSaveButtonToRequest || !!buttonContinueHint}
                    offset={10}
                    className="w-full"
                  >
                    <Button
                      className="w-full relative"
                      type="submit"
                      onClick={onClickContinue}
                      disabled={isLoading || disableNext}
                    >
                      {isUpdate && !isConfirmPage ? 'Save change' : buttonContinueName}
                    </Button>
                  </TooltipNew>
                }
                disabled={!buttonContinueHint}
              >
                <span>{buttonContinueHint}</span>
              </Tooltip>
            )}
            {onClickSkip && (
              <Button className="w-full" variant="secondary" type="submit" onClick={onClickSkip}>
                {buttonSkipName}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );

  if (formik) {
    return <FormikProvider value={formik}>{Body}</FormikProvider>;
  }

  return <>{Body}</>;
};
